import { useMemo } from 'react';
import { AppError } from '@mews-framework/errors';
import { createSafeContext } from '@mews-framework/utils';
import { DateTimeLocalizationInfo } from './types';

interface GlobalizationContextValue {
    timezone: string;
    dateTimeLocalizationInfo: DateTimeLocalizationInfo;
    languageCode: string;
    textLocalizationInfo?: { languageCodes: string[] };
}

const { GlobalizationContextProvider: BaseGlobalizationContextProvider, useGlobalizationContext } = createSafeContext<
    'GlobalizationContext',
    GlobalizationContextValue
>('GlobalizationContext');

interface GlobalizationContextProviderProps {
    timezone: string;
    dateTimeLocalizationInfo: DateTimeLocalizationInfo;
    languageCode: string;
    textLocalizationInfo?: { languageCodes: string[] };
}

export const GlobalizationContextProvider: React.FC<React.PropsWithChildren<GlobalizationContextProviderProps>> = ({
    timezone,
    dateTimeLocalizationInfo,
    languageCode,
    textLocalizationInfo,
    children,
}) => {
    const value = useMemo(
        () => ({
            timezone,
            dateTimeLocalizationInfo,
            languageCode,
            textLocalizationInfo,
        }),
        [timezone, dateTimeLocalizationInfo, languageCode, textLocalizationInfo]
    );

    return <BaseGlobalizationContextProvider value={value}>{children}</BaseGlobalizationContextProvider>;
};

export const useTimezone = () => useGlobalizationContext().timezone;
export const useDateTimeLocalizationInfo = () => useGlobalizationContext().dateTimeLocalizationInfo;
export const useLanguageCode = () => useGlobalizationContext().languageCode;
export const useTextLocalizationInfo = () => {
    const { textLocalizationInfo } = useGlobalizationContext();

    if (!textLocalizationInfo) {
        throw new AppError('textLocalizationInfo is not provided in the context');
    }

    return textLocalizationInfo;
};
